import React, { useState } from "react";
import { useAppSelector } from "../../../shared/store/hooks";
import { executeQuery } from "../api";
import { CUBE_NAME } from "../cubeSlice";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import {
  ColumnFilterPanel,
  CHART_COLORS,
  isExcludedMember,
} from "./PeriodDashboard";
import { FONTS, colors } from "../../../shared/ui/tokens";
import { CUBE_DIMENSIONS } from "../dimensions";

// ── AVDASH brand palette ──────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  teal: colors.accent[300],
  tealLight: colors.accent[100],
  orange: colors.warning[600],
  orangeLight: colors.warning[50],
};

const COLORS = [
  "#2B5CE6",
  "#3ECFBF",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EA580C",
  "#7C3AED",
  "#BE185D",
  "#047857",
  "#B45309",
];

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: NW.white,
  borderRadius: 14,
  border: `1.5px solid ${NW.gray200}`,
  boxShadow: "0 2px 16px rgba(24,75,140,0.07)",
  ...extra,
});

// ── Types ─────────────────────────────────────────────────────────────────────
type Granularity = "day" | "week" | "month" | "year";
type ChartType = "line" | "bar" | "area";

const TYPE_OPTIONS: { type: ChartType; icon: string; label: string }[] = [
  { type: "line", icon: "📈", label: "Line" },
  { type: "bar", icon: "📊", label: "Bar" },
  { type: "area", icon: "🏔️", label: "Area" },
];
const GRAN_OPTIONS: { value: Granularity; label: string }[] = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
  { value: "year", label: "Année" },
];
const TOP_OPTIONS = [5, 10, 20, 50];

// ── Date helpers ──────────────────────────────────────────────────────────────
function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function startOfWeek(d: Date): Date {
  const r = new Date(d);
  const day = r.getDay();
  r.setDate(r.getDate() - day + (day === 0 ? -6 : 1));
  return r;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

interface Interval {
  label: string;
  from: string;
  to: string;
}

function buildIntervals(
  from: string,
  to: string,
  gran: Granularity,
): Interval[] {
  const intervals: Interval[] = [];
  const end = new Date(to);
  let cur = new Date(from);
  while (cur <= end) {
    let intervalEnd: Date;
    let label: string;
    if (gran === "day") {
      intervalEnd = new Date(cur);
      label = fmt(cur);
    } else if (gran === "week") {
      cur = startOfWeek(cur);
      intervalEnd = addDays(cur, 6);
      label = `S ${fmt(cur)}`;
    } else if (gran === "month") {
      cur = startOfMonth(cur);
      intervalEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      label = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
    } else {
      cur = startOfYear(cur);
      intervalEnd = new Date(cur.getFullYear(), 11, 31);
      label = String(cur.getFullYear());
    }
    const clampedEnd = intervalEnd > end ? end : intervalEnd;
    intervals.push({ label, from: fmt(cur), to: fmt(clampedEnd) });
    if (gran === "day") cur = addDays(cur, 1);
    else if (gran === "week") cur = addDays(intervalEnd, 1);
    else if (gran === "month")
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    else cur = new Date(cur.getFullYear() + 1, 0, 1);
  }
  return intervals;
}

function splitColHeader(
  header: string,
  knownMeasures: string[],
): [string, string] {
  const m = knownMeasures.find((mn) => header.startsWith(mn + " - "));
  if (m) return [header.slice(m.length + 3), m];
  const sep = header.indexOf(" - ");
  if (sep >= 0) return [header.substring(sep + 3), header.substring(0, sep)];
  return [header, header];
}

function sumColData(data: any, colIdx: number): number {
  if (!data?.rows?.length) return 0;
  let s = 0;
  for (const row of data.rows) {
    const v = row[colIdx];
    if (typeof v === "number") s += Math.abs(v);
  }
  return s;
}

function applyTopN(
  pts: { name: string; [k: string]: any }[],
  series: string[],
  topN: number | null,
): string[] {
  if (!topN || topN >= series.length) return series;
  const totals: Record<string, number> = {};
  series.forEach((s) => {
    totals[s] = pts.reduce(
      (sum, pt) => sum + (typeof pt[s] === "number" ? pt[s] : 0),
      0,
    );
  });
  return [...series].sort((a, b) => totals[b] - totals[a]).slice(0, topN);
}

// ── Tooltip style ─────────────────────────────────────────────────────────────
const tooltipStyle: React.CSSProperties = {
  borderRadius: 10,
  border: `1px solid ${NW.violetMed}`,
  fontFamily: FONTS.body,
  fontSize: 12,
  boxShadow: "0 4px 20px rgba(24,75,140,0.15)",
};

// ── Top N selector ────────────────────────────────────────────────────────────
interface TopNSelectorProps {
  topN: number | null;
  onChange: (n: number | null) => void;
}
const TopNSelector: React.FC<TopNSelectorProps> = ({ topN, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 5,
      background: NW.gray50,
      borderRadius: 10,
      padding: "4px 10px",
      border: `1.5px solid ${NW.gray200}`,
    }}
  >
    <span style={{ fontSize: 12, marginRight: 2 }}>🏆</span>
    {TOP_OPTIONS.map((n) => (
      <button
        key={n}
        onClick={() => onChange(topN === n ? null : n)}
        style={{
          fontSize: 11,
          padding: "2px 9px",
          borderRadius: 99,
          cursor: "pointer",
          border: `1.5px solid ${topN === n ? NW.violet : "transparent"}`,
          background: topN === n ? NW.violet : "transparent",
          color: topN === n ? "#FFFFFF" : NW.gray500,
          fontWeight: topN === n ? 700 : 400,
          transition: "all 0.12s",
          fontFamily: FONTS.body,
        }}
      >
        {n}
      </button>
    ))}
    <input
      type="number"
      min={1}
      max={999}
      placeholder="N"
      value={topN !== null && !TOP_OPTIONS.includes(topN) ? topN : ""}
      onChange={(e) => {
        const v = parseInt(e.target.value);
        onChange(isNaN(v) || v < 1 ? null : v);
      }}
      style={{
        width: 40,
        border: `1px solid ${NW.gray200}`,
        borderRadius: 7,
        padding: "2px 5px",
        fontSize: 11,
        fontFamily: FONTS.body,
        outline: "none",
        textAlign: "center",
      }}
    />
    {topN !== null && (
      <button
        onClick={() => onChange(null)}
        style={{
          fontSize: 11,
          padding: "2px 9px",
          border: `1px solid ${NW.gray200}`,
          borderRadius: 7,
          cursor: "pointer",
          background: NW.white,
          color: NW.gray500,
          fontFamily: FONTS.body,
        }}
      >
        Tout
      </button>
    )}
  </div>
);

// ── EvoChart ──────────────────────────────────────────────────────────────────
interface EvoChartProps {
  data: { name: string; [k: string]: any }[];
  series: string[];
  chartType: ChartType;
}
const EvoChart: React.FC<EvoChartProps> = ({ data, series, chartType }) => {
  const children = [
    <CartesianGrid
      key="g"
      strokeDasharray="3 3"
      stroke={NW.gray100}
      vertical={false}
    />,
    <XAxis
      key="x"
      dataKey="name"
      tick={{ fontSize: 10, fill: NW.gray700, fontFamily: FONTS.body }}
      interval="preserveStartEnd"
    />,
    <YAxis
      key="y"
      tick={{ fontSize: 11, fill: NW.gray500, fontFamily: FONTS.body }}
    />,
    <Tooltip
      key="t"
      formatter={(v: any) =>
        typeof v === "number" ? v.toLocaleString("fr-FR") : v
      }
      contentStyle={tooltipStyle}
    />,
    <Legend key="l" wrapperStyle={{ fontFamily: FONTS.body, fontSize: 12 }} />,
    ...series.map((s, i) => {
      const color = COLORS[i % COLORS.length];
      if (chartType === "bar")
        return <Bar key={s} dataKey={s} fill={color} radius={[4, 4, 0, 0]} />;
      if (chartType === "area")
        return (
          <Area
            key={s}
            type="monotone"
            dataKey={s}
            stroke={color}
            fill={color}
            fillOpacity={0.15}
            strokeWidth={2.5}
          />
        );
      return (
        <Line
          key={s}
          type="monotone"
          dataKey={s}
          stroke={color}
          dot={data.length < 60}
          strokeWidth={2.5}
        />
      );
    }),
  ];
  return (
    <ResponsiveContainer width="100%" height={320}>
      {chartType === "bar" ? (
        <BarChart data={data}>{children}</BarChart>
      ) : chartType === "area" ? (
        <AreaChart data={data}>{children}</AreaChart>
      ) : (
        <LineChart data={data}>{children}</LineChart>
      )}
    </ResponsiveContainer>
  );
};

// ── ColEvoPanel ───────────────────────────────────────────────────────────────
interface ColEvoData {
  colMember: string;
  byInterval: any[];
  series: string[];
}

interface ColEvoPanelProps {
  colData: ColEvoData;
  accentColor: string;
  dateFrom: string;
  dateTo: string;
  rows: any[];
  columns: any[];
  selMeasures: string[];
  measureCaptions: Record<string, string>;
  rowDimCount: number;
}

const ColEvoPanel: React.FC<ColEvoPanelProps> = ({
  colData,
  accentColor,
  dateFrom,
  dateTo,
  rows,
  columns,
  selMeasures,
  measureCaptions,
  rowDimCount,
}) => {
  const [gran, setGran] = useState<Granularity>("month");
  const [chartType, setChartType] = useState<ChartType>("line");
  const [topN, setTopN] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allPts, setAllPts] = useState<{ name: string; [k: string]: any }[]>(
    [],
  );
  const [allSeries, setAllSeries] = useState<string[]>([]);

  const visibleSeries = applyTopN(allPts, allSeries, topN);

  const run = async () => {
    if (!dateFrom || !dateTo) {
      setError("Sélectionnez une période.");
      return;
    }
    const intervals = buildIntervals(dateFrom, dateTo, gran);
    if (intervals.length > 400) {
      setError("Trop d'intervalles.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const BATCH = 20;
      const results: { label: string; data: any }[] = [];
      for (let i = 0; i < intervals.length; i += BATCH) {
        const batch = intervals.slice(i, i + BATCH);
        const fetched = await Promise.all(
          batch.map((iv) =>
            executeQuery({
              cubeName: CUBE_NAME,
              measures: selMeasures,
              rows,
              columns,
              dateFrom: iv.from,
              dateTo: iv.to,
            }).then((r) => ({ label: iv.label, data: r.data })),
          ),
        );
        results.push(...fetched);
      }
      const firstData = results.find((r) => r.data?.columns?.length > 0)?.data;
      if (!firstData) {
        setError("Aucun résultat.");
        return;
      }

      const dataColHeaders: string[] = firstData.columns.slice(rowDimCount);
      const colIdxs: { caption: string; idx: number }[] = [];
      dataColHeaders.forEach((header, hi) => {
        const [colMember, measurePart] = splitColHeader(header, selMeasures);
        if (colMember === colData.colMember)
          colIdxs.push({
            caption: measureCaptions[measurePart] ?? measurePart,
            idx: hi,
          });
      });

      if (rows.length > 0) {
        const rowMemberSet = new Set<string>();
        results.forEach(({ data }) => {
          data?.rows?.forEach((row: any[]) => {
            const member = String(row[rowDimCount - 1] ?? row[0] ?? "");
            if (!isExcludedMember(member)) {
              // ✅ exclure all/unknown/inconnue/vide
              rowMemberSet.add(member);
            }
          });
        });
        const rowMembers = Array.from(rowMemberSet);
        const pts = results.map(({ label, data }) => {
          const e: { name: string; [k: string]: any } = { name: label };
          rowMembers.forEach((member) => {
            const row = data?.rows?.find(
              (r: any[]) => String(r[rowDimCount - 1] ?? r[0] ?? "") === member,
            );
            e[member] = colIdxs.reduce((sum, c) => {
              const v = row?.[rowDimCount + c.idx];
              return sum + (typeof v === "number" ? Math.abs(v) : 0);
            }, 0);
          });
          return e;
        });
        setAllPts(pts);
        setAllSeries(rowMembers);
      } else {
        const seriesKey = colData.colMember;
        const pts = results.map(({ label, data }) => ({
          name: label,
          [seriesKey]: colIdxs.reduce(
            (sum, c) => sum + sumColData(data, rowDimCount + c.idx),
            0,
          ),
        }));
        setAllPts(pts);
        setAllSeries([seriesKey]);
      }
    } catch (e: any) {
      setError(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-relancer run() quand gran change, si des données existent déjà
  const runRef = React.useRef<() => Promise<void>>(run);
  React.useEffect(() => {
    runRef.current = run;
  });
  React.useEffect(() => {
    if (allPts.length > 0) {
      runRef.current();
    }
  }, [gran]); // eslint-disable-line

  return (
    <div style={card({ overflow: "hidden", borderColor: `${accentColor}44` })}>
      {/* Panel header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${accentColor}, ${accentColor}CC)`,
          color: "#FFFFFF",
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span
          style={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 14 }}
        >
          {colData.colMember}
        </span>
        {allPts.length > 0 && (
          <span
            style={{
              fontSize: 11,
              opacity: 0.8,
              marginLeft: "auto",
              background: "rgba(255,255,255,0.15)",
              borderRadius: 8,
              padding: "2px 10px",
            }}
          >
            {allPts.length} points
          </span>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          padding: "10px 14px",
          background: `linear-gradient(90deg, ${NW.violetSoft}, ${NW.white})`,
          borderBottom: `1.5px solid ${NW.violetMed}`,
        }}
      >
        {/* Granularity toggle */}
        <div
          style={{
            display: "flex",
            border: `1.5px solid ${NW.gray200}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          {GRAN_OPTIONS.map((g) => (
            <button
              key={g.value}
              onClick={() => setGran(g.value)}
              style={{
                padding: "4px 11px",
                fontSize: 11,
                border: "none",
                cursor: "pointer",
                background: gran === g.value ? accentColor : NW.white,
                color: gran === g.value ? "#FFFFFF" : NW.gray500,
                fontWeight: gran === g.value ? 700 : 400,
                fontFamily: FONTS.body,
                transition: "all 0.12s",
              }}
            >
              {g.label}
            </button>
          ))}
        </div>

        {/* Chart type toggle */}
        <div
          style={{
            display: "flex",
            border: `1.5px solid ${NW.gray200}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.type}
              onClick={() => setChartType(opt.type)}
              title={opt.label}
              style={{
                padding: "4px 10px",
                fontSize: 13,
                border: "none",
                cursor: "pointer",
                background:
                  chartType === opt.type
                    ? NW.violet
                    : NW.white,
                color: chartType === opt.type ? "#FFFFFF" : NW.gray500,
                transition: "all 0.15s",
              }}
            >
              {opt.icon}
            </button>
          ))}
        </div>

        {/* Top N — only when rows selected */}
        {rows.length > 0 && <TopNSelector topN={topN} onChange={setTopN} />}

        {/* Run button */}
        <button
          onClick={run}
          disabled={loading}
          style={{
            padding: "6px 16px",
            background: loading
              ? NW.gray200
              : NW.violet,
            color: loading ? NW.gray500 : "#FFFFFF",
            border: "none",
            borderRadius: 10,
            cursor: loading ? "default" : "pointer",
            fontWeight: 700,
            fontSize: 12,
            fontFamily: FONTS.body,
            boxShadow: loading ? "none" : `0 3px 12px rgba(24,75,140,0.25)`,
            transition: "all 0.15s",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {loading ? "⏳ …" : "▶ Calculer"}
        </button>

        {allPts.length > 0 && topN && (
          <span
            style={{
              fontSize: 11,
              color: NW.gray500,
              padding: "3px 10px",
              background: NW.violetSoft,
              border: `1px solid ${NW.violetMed}`,
              borderRadius: 99,
            }}
          >
            Top {topN} / {allSeries.length}
          </span>
        )}
      </div>

      {/* Chart area */}
      <div style={{ padding: 16 }}>
        {error && (
          <div
            style={{
              color: NW.danger,
              fontSize: 12,
              marginBottom: 10,
              padding: "8px 12px",
              background: NW.dangerLight,
              borderRadius: 8,
              border: `1px solid ${NW.danger}30`,
            }}
          >
            ⚠ {error}
          </div>
        )}
        {allPts.length > 0 ? (
          <EvoChart
            data={allPts}
            series={visibleSeries}
            chartType={chartType}
          />
        ) : (
          <div
            style={{
              color: NW.gray500,
              fontSize: 13,
              padding: 32,
              textAlign: "center",
              border: `1.5px dashed ${NW.violetMed}`,
              borderRadius: 10,
            }}
          >
            Cliquez <strong style={{ color: NW.violet }}>▶ Calculer</strong>{" "}
            pour afficher l'évolution
          </div>
        )}
      </div>
    </div>
  );
};

// ── EvolutionView ─────────────────────────────────────────────────────────────
// APRÈS
interface EvolutionViewProps {
  defaultMeasure?: string;
  defaultDimension?: string;
  defaultHierarchy?: string;
  defaultLevel?: string;
  // ── Overrides pour les vues locales (MagasinView, FamilleView…) ──────────
  overrideMeasures?: string[];
  overrideDateFrom?: string;
  overrideDateTo?: string;
  overrideMagasinFilter?: string;
  overrideDepotFilter?: string[];
  overrideSaisonFilter?: string[];
  overrideCouleurFilter?: string[];
  overrideFamilleFilter?: string[];
  overrideSousFamilleFilter?: string[];
  overrideTailleFilter?: string[];
  overrideVendeurFilter?: string[];
  overrideVentesFilter?: string[];
  overrideMarqueFilter?: string[];
}

const EvolutionView: React.FC<EvolutionViewProps> = ({
  defaultMeasure,
  defaultDimension,
  defaultHierarchy,
  defaultLevel,
  overrideMeasures,
  overrideDateFrom,
  overrideDateTo,
  overrideMagasinFilter,
  overrideDepotFilter,
  overrideSaisonFilter,
  overrideCouleurFilter,
  overrideFamilleFilter,
  overrideSousFamilleFilter,
  overrideTailleFilter,
  overrideVendeurFilter,
  overrideVentesFilter,
  overrideMarqueFilter,
}) => {
  const {
    measures: reduxMeasures,
    rows,
    columns,
    dateFrom: reduxDateFrom,
    dateTo: reduxDateTo,
  } = useAppSelector((s) => s.query);
  const allMeasures = useAppSelector((s) => s.cube.measures);

  // Utiliser les overrides si fournis, sinon Redux
  const selMeasures = overrideMeasures ?? reduxMeasures;
  const dateFrom = overrideDateFrom ?? reduxDateFrom;
  const dateTo = overrideDateTo ?? reduxDateTo;

  const [gran, setGran] = useState<Granularity>("month");
  const [chartType, setChartType] = useState<ChartType>("line");
  const [topN, setTopN] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ranInfo, setRanInfo] = useState("");
  const [allPts, setAllPts] = useState<{ name: string; [k: string]: any }[]>(
    [],
  );
  const [allSeries, setAllSeries] = useState<string[]>([]);
  const [colMembers, setColMembers] = useState<string[]>([]);
  const [colData, setColData] = useState<ColEvoData[]>([]);
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const measureCaptions: Record<string, string> = {};
  allMeasures.forEach((m) => {
    measureCaptions[m.name] = m.caption;
  });

  const hasRows = rows.length > 0;
  const hasCols = columns.length > 0;
  const visibleSeries = applyTopN(allPts, allSeries, topN);

  // Auto-relancer discover() si les filtres ou mesures changent
  const prevFiltersRef = React.useRef<string>("");
  React.useEffect(() => {
    const key = JSON.stringify({
      selMeasures,
      dateFrom,
      dateTo,
      gran, // ← ajouté ✅
      overrideMagasinFilter,
      overrideDepotFilter,
      overrideSaisonFilter,
      overrideCouleurFilter,
      overrideFamilleFilter,
      overrideSousFamilleFilter,
      overrideTailleFilter,
      overrideVendeurFilter,
      overrideVentesFilter,
      overrideMarqueFilter,
    });
    if (
      key !== prevFiltersRef.current &&
      allPts.length > 0 // seulement si une courbe existe déjà
    ) {
      prevFiltersRef.current = key;
      discover();
    }
  }, [
    selMeasures,
    dateFrom,
    dateTo,
    gran, // ← ajouté ✅
    overrideMagasinFilter,
    overrideDepotFilter,
    overrideSaisonFilter,
    overrideCouleurFilter,
    overrideFamilleFilter,
    overrideSousFamilleFilter,
    overrideTailleFilter,
    overrideVendeurFilter,
    overrideVentesFilter,
    overrideMarqueFilter,
  ]); // eslint-disable-line

  const discover = async () => {
    if (!dateFrom || !dateTo) {
      setError("Sélectionnez une période (De / À).");
      return;
    }
    if (selMeasures.length === 0) {
      setError("Sélectionnez au moins une mesure.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Ajouter juste avant la fonction discover() :
      const activeDepots: string[] | null = overrideMagasinFilter
        ? [overrideMagasinFilter]
        : overrideDepotFilter && overrideDepotFilter.length > 0
          ? overrideDepotFilter
          : null;

      const activeMembers = overrideSaisonFilter?.length
        ? overrideSaisonFilter
        : overrideCouleurFilter?.length
          ? overrideCouleurFilter
          : overrideFamilleFilter?.length
            ? overrideFamilleFilter
            : overrideSousFamilleFilter?.length
              ? overrideSousFamilleFilter
              : overrideTailleFilter?.length
                ? overrideTailleFilter
                : overrideVendeurFilter?.length
                  ? overrideVendeurFilter
                  : overrideVentesFilter?.length
                    ? overrideVentesFilter
                    : overrideMarqueFilter?.length
                      ? overrideMarqueFilter
                      : null;

      // Si filtre membre actif → ajouter sa dimension en rows
      const memberRows = activeMembers
        ? [
            {
              dimensionName: defaultDimension!,
              hierarchyName: defaultHierarchy!,
              levelName: defaultLevel!,
            },
          ]
        : rows;

      const magasinColumns = activeDepots
        ? [
            {
              ...CUBE_DIMENSIONS.depot,
            },
          ]
        : columns;

      const res = await executeQuery({
        cubeName: CUBE_NAME,
        measures: selMeasures,
        rows: memberRows,
        columns: magasinColumns,
        dateFrom,
        dateTo,
      });
      const data = res.data;
      const rowDimCount = activeMembers
        ? 1 // dimension membre en rows → 1 colonne dimension avant les mesures
        : hasRows
          ? Math.min(rows.length, data.columns.length)
          : 0;
      let dataColHeaders: string[] = data.columns.slice(rowDimCount);

      // Si filtre magasin actif → garder seulement les colonnes du dépôt choisi
      if (overrideMagasinFilter) {
        dataColHeaders = dataColHeaders.filter((col) => {
          const depotPart = col.split(" - ").pop()?.trim() ?? "";
          return (
            depotPart.toLowerCase() === overrideMagasinFilter.toLowerCase()
          );
        });
      }
      if (!hasCols) {
        const intervals = buildIntervals(dateFrom, dateTo, gran);
        if (intervals.length > 400) {
          setError("Trop d'intervalles.");
          return;
        }
        const BATCH = 20;
        const results: { label: string; data: any }[] = [];
        for (let i = 0; i < intervals.length; i += BATCH) {
          const batch = intervals.slice(i, i + BATCH);
          const fetched = await Promise.all(
            batch.map((iv) =>
              executeQuery({
                cubeName: CUBE_NAME,
                measures: selMeasures,
                rows: memberRows,
                columns: magasinColumns,
                dateFrom: iv.from,
                dateTo: iv.to,
              }).then((r) => ({ label: iv.label, data: r.data })),
            ),
          );
          results.push(...fetched);
        }
        setRanInfo(`${intervals.length} intervalles · ${dateFrom} → ${dateTo}`);

        if (hasRows) {
        } else {
          const series = selMeasures.map((m) => measureCaptions[m] ?? m);
          const pts = results.map(({ label, data: d }) => {
            const e: { name: string; [k: string]: any } = { name: label };

            if (overrideMagasinFilter) {
              // Filtrer uniquement les colonnes du dépôt sélectionné
              const allCols: string[] = d.columns ?? [];
              selMeasures.forEach((m) => {
                const caption = measureCaptions[m] ?? m;
                let total = 0;
                allCols.forEach((col: string, ci: number) => {
                  if (ci < rowDimCount) return;
                  const depotPart = col.split(" - ").pop()?.trim() ?? "";
                  const measurePart = col
                    .split(" - ")
                    .slice(0, -1)
                    .join(" - ")
                    .trim();
                  const matchDepot =
                    depotPart.toLowerCase() ===
                    overrideMagasinFilter.toLowerCase();
                  const matchMeasure =
                    measurePart.toLowerCase() === m.toLowerCase() ||
                    measurePart.toLowerCase() === caption.toLowerCase();
                  if (matchDepot && matchMeasure) {
                    const v = d.rows?.[0]?.[ci];
                    total += typeof v === "number" ? Math.abs(v) : 0;
                  }
                });
                e[caption] = total;
              });
            } else {
              const activeMembers = overrideSaisonFilter?.length
                ? overrideSaisonFilter
                : overrideCouleurFilter?.length
                  ? overrideCouleurFilter
                  : overrideFamilleFilter?.length
                    ? overrideFamilleFilter
                    : overrideSousFamilleFilter?.length
                      ? overrideSousFamilleFilter
                      : overrideTailleFilter?.length
                        ? overrideTailleFilter
                        : overrideVendeurFilter?.length
                          ? overrideVendeurFilter
                          : overrideVentesFilter?.length
                            ? overrideVentesFilter
                            : overrideMarqueFilter?.length
                              ? overrideMarqueFilter
                              : null;

              const activeDepots = overrideMagasinFilter
                ? [overrideMagasinFilter]
                : overrideDepotFilter?.length
                  ? overrideDepotFilter
                  : null;

              selMeasures.forEach((m) => {
                const caption = measureCaptions[m] ?? m;
                let total = 0;

                if (activeMembers) {
                  (d.rows ?? []).forEach((row: any[]) => {
                    const memberName = String(row[0] ?? "").trim();
                    if (
                      !activeMembers.some(
                        (mem) => mem.toLowerCase() === memberName.toLowerCase(),
                      )
                    )
                      return;
                    const mi = selMeasures.indexOf(m);
                    const v = row[1 + mi]; // 1 = colonne dimension, puis mesures
                    total += typeof v === "number" ? Math.abs(v) : 0;
                  });
                  e[caption] = total;
                } else if (activeDepots) {
                  (d.columns as string[]).forEach((col: string, ci: number) => {
                    if (ci < rowDimCount) return;
                    const parts = col.split(" - ");
                    const depotPart = parts[parts.length - 1].trim();
                    const measurePart = parts
                      .slice(0, parts.length - 1)
                      .join(" - ")
                      .trim();
                    if (
                      activeDepots.some(
                        (dep) => dep.toLowerCase() === depotPart.toLowerCase(),
                      ) &&
                      (measurePart.toLowerCase() === m.toLowerCase() ||
                        measurePart.toLowerCase() === caption.toLowerCase())
                    ) {
                      const v = d.rows?.[0]?.[ci];
                      total += typeof v === "number" ? Math.abs(v) : 0;
                    }
                  });
                  e[caption] = total;
                } else {
                  const mi = selMeasures.indexOf(m);
                  e[caption] = sumColData(d, rowDimCount + mi);
                }
              });
            }
            return e;
          });
          setAllPts(pts);
          setAllSeries(series);
        }
        setColMembers([]);
        setColData([]);
        return;
      }

      // Col dim: discover members
      const memberMap = new Map<string, string[]>();
      dataColHeaders.forEach((header) => {
        const [colMember, measurePart] = splitColHeader(header, selMeasures);
        if (!memberMap.has(colMember)) memberMap.set(colMember, []);
        memberMap
          .get(colMember)!
          .push(measureCaptions[measurePart] ?? measurePart);
      });
      const members = Array.from(memberMap.keys()).filter(
        (m) => !isExcludedMember(m),
      );
      setColMembers(members);
      setColData(
        members.map((m) => ({
          colMember: m,
          byInterval: [],
          series: hasRows ? [] : [m],
        })),
      );
      setSelected(null);
      setAllPts([]);
      setAllSeries([]);
      setRanInfo(`${members.length} colonnes découvertes`);
    } catch (e: any) {
      setError(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const effectiveSelected = selected === null ? new Set(colMembers) : selected;
  const toggle = (v: string) => {
    const base = selected === null ? new Set(colMembers) : new Set(selected);
    if (base.has(v)) base.delete(v);
    else base.add(v);
    setSelected(base);
  };
  const rowDimCount = hasRows ? Math.min(rows.length, 99) : 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        fontFamily: FONTS.body,
      }}
    >
      {/* ── Page header ── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 55%, #1D5BA8 100%)`,
          borderRadius: 16,
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          boxShadow: "0 6px 32px rgba(24,75,140,0.28)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${NW.roseMed}80, ${NW.roseMed}CC, ${NW.roseMed}80, transparent)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            flexShrink: 0,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          📈
        </div>
        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 18,
              color: "#FFFFFF",
              letterSpacing: -0.4,
            }}
          >
            Évolution Temporelle
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            Tendances · Granularité · Multi-séries
          </div>
        </div>
        {ranInfo && (
          <div
            style={{
              marginLeft: "auto",
              padding: "6px 14px",
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 10,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.75)",
                fontWeight: 600,
              }}
            >
              {ranInfo}
            </span>
          </div>
        )}
      </div>

      {/* ── Global controls card ── */}
      <div style={card({ padding: "14px 18px" })}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {/* Granularity — only when no col dimension */}
          {!hasCols && (
            <>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: NW.violet,
                    textTransform: "uppercase",
                    letterSpacing: 1.5,
                    marginBottom: 6,
                  }}
                >
                  📆 Granularité
                </div>
                <div
                  style={{
                    display: "flex",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 10,
                    overflow: "hidden",
                  }}
                >
                  {GRAN_OPTIONS.map((g) => (
                    <button
                      key={g.value}
                      onClick={() => setGran(g.value)}
                      style={{
                        padding: "5px 13px",
                        fontSize: 12,
                        border: "none",
                        cursor: "pointer",
                        background:
                          gran === g.value
                            ? NW.violet
                            : NW.white,
                        color: gran === g.value ? "#FFFFFF" : NW.gray500,
                        fontWeight: gran === g.value ? 700 : 400,
                        fontFamily: FONTS.body,
                        transition: "all 0.12s",
                      }}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div
                style={{
                  width: 1,
                  height: 44,
                  background: NW.gray200,
                  flexShrink: 0,
                }}
              />

              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: NW.rose,
                    textTransform: "uppercase",
                    letterSpacing: 1.5,
                    marginBottom: 6,
                  }}
                >
                  📊 Type de graphique
                </div>
                <div
                  style={{
                    display: "flex",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 10,
                    overflow: "hidden",
                  }}
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.type}
                      onClick={() => setChartType(opt.type)}
                      title={opt.label}
                      style={{
                        padding: "5px 13px",
                        fontSize: 13,
                        border: "none",
                        cursor: "pointer",
                        background:
                          chartType === opt.type
                            ? NW.violet
                            : NW.white,
                        color: chartType === opt.type ? "#FFFFFF" : NW.gray500,
                        transition: "all 0.15s",
                      }}
                    >
                      {opt.icon}{" "}
                      <span style={{ fontSize: 11, fontFamily: FONTS.body }}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {hasRows && (
                <>
                  <div
                    style={{
                      width: 1,
                      height: 44,
                      background: NW.gray200,
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: NW.teal,
                        textTransform: "uppercase",
                        letterSpacing: 1.5,
                        marginBottom: 6,
                      }}
                    >
                      🏆 Top N
                    </div>
                    <TopNSelector topN={topN} onChange={setTopN} />
                  </div>
                </>
              )}
            </>
          )}

          {/* Discover / Run button */}
          <button
            onClick={discover}
            disabled={loading}
            style={{
              marginLeft: !hasCols ? "auto" : undefined,
              padding: "9px 22px",
              background: loading
                ? NW.gray200
                : NW.violet,
              color: loading ? NW.gray500 : "#FFFFFF",
              border: "none",
              borderRadius: 10,
              cursor: loading ? "default" : "pointer",
              fontWeight: 700,
              fontSize: 13,
              fontFamily: FONTS.body,
              boxShadow: loading ? "none" : `0 4px 16px rgba(24,75,140,0.28)`,
              transition: "all 0.15s",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {loading
              ? "⏳ Chargement…"
              : hasCols
                ? "🔍 Découvrir les colonnes"
                : "▶ Calculer évolution"}
          </button>

          {/* ── Bouton Enregistrer ── */}
          <button
            onClick={() => setShowSaveModal(true)}
            title="Enregistrer cette évolution"
            style={{
              padding: "9px 18px",
              background: NW.violet,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 10,
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 13,
              fontFamily: FONTS.body,
              boxShadow: `0 4px 16px rgba(24,75,140,0.28)`,
              transition: "all 0.15s",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            💾 Enregistrer
          </button>

          {/* Warnings */}
          {!dateFrom && (
            <div
              style={{
                fontSize: 12,
                color: NW.danger,
                padding: "5px 12px",
                background: NW.dangerLight,
                borderRadius: 99,
                border: `1px solid ${NW.danger}30`,
              }}
            >
              ⚠ Sélectionnez une période
            </div>
          )}
          {selMeasures.length === 0 && (
            <div
              style={{
                fontSize: 12,
                color: NW.danger,
                padding: "5px 12px",
                background: NW.dangerLight,
                borderRadius: 99,
                border: `1px solid ${NW.danger}30`,
              }}
            >
              ⚠ Sélectionnez une mesure
            </div>
          )}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div
          style={{
            color: NW.danger,
            fontSize: 13,
            padding: "10px 16px",
            background: NW.dangerLight,
            borderRadius: 10,
            border: `1px solid ${NW.danger}30`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {/* ── Simple evolution chart ── */}
      {!hasCols && allPts.length > 0 && (
        <div style={card({ padding: 20 })}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: NW.violet,
                  boxShadow: `0 0 8px ${NW.violet}66`,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: FONTS.heading,
                  fontWeight: 800,
                  fontSize: 14,
                  color: NW.violetDark,
                }}
              >
                Évolution — {visibleSeries.join(", ")}
              </span>
            </div>
            {topN && allSeries.length > topN && (
              <span
                style={{
                  fontSize: 11,
                  color: NW.violet,
                  fontWeight: 700,
                  background: NW.violetLight,
                  border: `1px solid ${NW.violetMed}`,
                  borderRadius: 99,
                  padding: "3px 10px",
                }}
              >
                Top {topN} / {allSeries.length}
              </span>
            )}
          </div>
          <EvoChart
            data={allPts}
            series={visibleSeries}
            chartType={chartType}
          />
        </div>
      )}

      {/* ── Col dimension panels ── */}
      {hasCols && colMembers.length > 0 && (
        <>
          {/* ── Column selector — même composant que le Dashboard ── */}
          <ColumnFilterPanel
            activeMembers={colMembers}
            effectiveSelected={effectiveSelected}
            onToggle={toggle}
            onSelectAll={() => setSelected(null)}
            onSelectNone={() => setSelected(new Set())}
          />

          {effectiveSelected.size === 0 ? (
            <div
              style={{
                color: NW.gray500,
                padding: 20,
                fontSize: 13,
                textAlign: "center",
                fontFamily: FONTS.body,
              }}
            >
              Sélectionnez au moins une colonne.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {colData
                .filter((cd) => effectiveSelected.has(cd.colMember))
                .map((cd) => (
                  <ColEvoPanel
                    key={cd.colMember}
                    colData={cd}
                    accentColor={
                      CHART_COLORS[
                        colMembers.indexOf(cd.colMember) % CHART_COLORS.length
                      ]
                    }
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    rows={rows}
                    columns={columns}
                    selMeasures={selMeasures}
                    measureCaptions={measureCaptions}
                    rowDimCount={rowDimCount}
                  />
                ))}
            </div>
          )}
        </>
      )}

      {/* ── Modal Enregistrer ── */}
      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          type="mdx"
          section="evolution"
          overrideMeasures={selMeasures}
          overrideDateFrom={dateFrom}
          overrideDateTo={dateTo}
          overrideEvolutionConfig={
            dateFrom && dateTo ? { dateFrom, dateTo, granularity: gran } : null
          }
        />
      )}
    </div>
  );
};

export default EvolutionView;
